import { useCallback, useEffect, useRef, useState } from 'react';
import { getDepositoCobrancas } from '@spark/services/deposito';
import { mapCobrancaListItem, normalizeCobrancasDepositoFilters } from '../utils/cobrancasDepositoUtils';

const initialFilters = {
  NumeroNF: '',
  NumeroPedidoTMS: '',
  CNPJCliente: '',
  NomeCliente: '',
  TipoCobranca: '',
  DataEmissaoInicio: '',
  DataEmissaoFim: '',
  UsuarioEmissao: ''
};

export const useDepositoCobrancasListagem = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 100
  });
  const [filtros, setFiltros] = useState(initialFilters);

  const isMountedRef = useRef(true);
  const didInitialLoadRef = useRef(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(
    async (filtrosParaBuscar = filtros) => {
      if (!isMountedRef.current) return;

      try {
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        setLoading(true);

        const response = await getDepositoCobrancas({
          PaginaAtual: paginationState.pageIndex + 1,
          QuantidadePorPagina: paginationState.pageSize,
          ...normalizeCobrancasDepositoFilters(filtrosParaBuscar)
        });

        if (!isMountedRef.current || requestId !== requestIdRef.current) return;

        const responseData = response?.data || response || {};
        const registros = Array.isArray(responseData.registros) ? responseData.registros : [];

        setData(registros.map(mapCobrancaListItem));
        setTotalRegistros(responseData.totalRegistros || 0);
      } catch (error) {
        if (!isMountedRef.current) return;
        console.error('Erro ao buscar cobranças de depósito:', error);
        setData([]);
        setTotalRegistros(0);
      } finally {
        if (!isMountedRef.current) return;
        setLoading(false);
        setFilterLoading(false);
      }
    },
    [filtros, paginationState.pageIndex, paginationState.pageSize]
  );

  useEffect(() => {
    if (!didInitialLoadRef.current) return;
    fetchData(filtros);
  }, [fetchData, filtros, paginationState.pageIndex, paginationState.pageSize]);

  useEffect(() => {
    if (didInitialLoadRef.current) return;
    didInitialLoadRef.current = true;
    fetchData(initialFilters);
  }, [fetchData]);

  const handleFilterApply = useCallback(
    (newFilters) => {
      setFiltros(newFilters);
      setFilterLoading(true);
      setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
      fetchData(newFilters);
    },
    [fetchData]
  );

  const handleLimparFiltros = useCallback(
    () => {
      setFiltros(initialFilters);
      setFilterLoading(true);
      setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
      fetchData(initialFilters);
    },
    [fetchData]
  );

  return {
    data,
    loading,
    filterLoading,
    totalRegistros,
    filtros,
    paginationState,
    setPaginationState,
    refreshData: () => fetchData(filtros),
    handleFilterApply,
    handleLimparFiltros
  };
};
